import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star, Phone, Mail, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { authHeaders } from "../lib/authRefresh";

const API = window.location.origin;
export const RET_STYLE = {
  eski: { c: "#EAB308", bg: "rgba(234,179,8,0.10)", label: "Daha önce gelmiş" },
  sadik: { c: "#10B981", bg: "rgba(16,185,129,0.10)", label: "Sadık müşteri (3+ geliş)" },
  uyari: { c: "#F43F5E", bg: "rgba(244,63,94,0.09)", label: "Geçmişte kötü yorum / no-show" },
  olasi: { c: "#94A3B8", bg: "rgba(148,163,184,0.10)", label: "Olası eşleşme (yalnızca isim + ülke)" },
};
const TIER = { kesin: "Kesin", yuksek: "Yüksek", olasi: "Olası" };
const fmt = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("tr-TR"); } catch { return d; } };

function Visit({ v }) {
  return (
    <div className={`px-5 py-3 border-b border-slate-100 ${v.tier === "olasi" ? "opacity-70" : ""}`} data-testid={`ret-visit-${v.booking_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-xs font-bold text-amber-600">{fmt(v.booking_date)}{v.booking_time ? ` · ${String(v.booking_time).slice(0, 5)}` : ""}</span>
        <span className="text-[10px] uppercase tracking-wide text-slate-400">{TIER[v.tier]} · {v.reason}</span>
      </div>
      <div className="text-sm text-slate-800 mt-0.5">{v.product_name || "-"} · {v.total_pax || 0} kişi{v.salon ? ` · ${v.salon}` : ""}{v.masa ? ` · Masa ${v.masa}` : ""}</div>
      <div className="text-xs text-slate-500 mt-0.5">
        {v.customer_name}{v.garson ? ` · Garson: ${v.garson}` : ""}{v.toplam || v.tutar ? ` · Tutar: ${v.toplam || v.tutar}` : ""}{v.tahsilat ? ` · ${v.tahsilat}` : ""}
      </div>
      {v.notes && <div className="text-xs text-slate-500 italic mt-0.5">Not: {v.notes}</div>}
      {v.reviews.map((r, i) => (
        <div key={i} className="mt-1 text-xs flex items-start gap-1.5">
          <span className={`inline-flex items-center gap-0.5 font-semibold ${r.rating <= 3 ? "text-rose-600" : "text-amber-500"}`}><Star className="w-3 h-3" fill="currentColor" />{r.rating}</span>
          <span className="text-slate-400 uppercase">{r.source}</span>
          {r.body && <span className="text-slate-600 line-clamp-2">"{r.body}"</span>}
        </div>
      ))}
      <a href={`/bookings/${v.booking_id}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline" data-testid={`ret-visit-link-${v.booking_id}`}><ExternalLink className="w-3 h-3" />{v.confirmation_code || "Rezervasyon"}</a>
    </div>
  );
}

export function ReturningCustomerPanel({ bookingId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!bookingId) return;
    setD(null); setErr(null);
    axios.get(`${API}/api/customers/history/${bookingId}`, { headers: authHeaders() })
      .then(r => setD(r.data)).catch(e => setErr(e?.response?.data?.detail || "Yüklenemedi"));
  }, [bookingId]);
  const s = d?.summary;
  const st = RET_STYLE[s?.level] || RET_STYLE.eski;
  return (
    <Sheet open={!!bookingId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-[420px] sm:max-w-[420px] p-0 bg-white overflow-y-auto" data-testid="returning-panel">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-100 text-left">
          <SheetTitle className="flex items-center gap-2 text-slate-900">
            {d?.booking.customer_name || "Müşteri geçmişi"}
            {s && <span className="inline-flex items-center justify-center rounded-md px-1.5 h-5 text-xs font-bold text-white" style={{ background: st.c }} data-testid="returning-panel-badge">E{s.visits}</span>}
          </SheetTitle>
          {s && <div className="text-xs text-slate-500" data-testid="returning-panel-level">{st.label} · Eşleşme: {s.reasons.join(", ")} · Güven: {TIER[s.tier]}</div>}
          {d && (
            <div className="text-xs text-slate-600 space-y-0.5 pt-1">
              {d.booking.customer_phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{d.booking.customer_phone}</div>}
              {d.booking.customer_email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{d.booking.customer_email}</div>}
            </div>
          )}
        </SheetHeader>
        {err && <div className="p-5 text-sm text-rose-600">{err}</div>}
        {!d && !err && <div className="p-5 text-sm text-slate-500">Yükleniyor…</div>}
        {d && (
          <>
            <div className="grid grid-cols-3 border-b border-slate-100 text-center">
              <div className="py-3 border-r border-slate-100"><div className="font-mono font-bold text-lg text-slate-900" data-testid="ret-stat-visits">{s?.visits || 0}</div><div className="text-[11px] text-slate-500">Önceki geliş</div></div>
              <div className="py-3 border-r border-slate-100"><div className="font-mono font-bold text-lg text-slate-900">{d.total_pax}</div><div className="text-[11px] text-slate-500">Toplam kişi</div></div>
              <div className="py-3"><div className="font-mono font-bold text-lg text-amber-500">{d.avg_rating != null ? `${d.avg_rating}★` : "—"}</div><div className="text-[11px] text-slate-500">Yorum ort.</div></div>
            </div>
            {d.visits.length === 0 ? <div className="p-5 text-sm text-slate-500">Geçmiş rezervasyon bulunamadı.</div> : d.visits.map(v => <Visit key={v.booking_id} v={v} />)}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
